import React, { useState, useCallback, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import {
  Download,
  FileSpreadsheet,
  Loader2,
  AlertCircle,
  Users,
  ShoppingCart,
  RefreshCw,
  Search,
  Euro,
  TicketIcon,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  CheckCircle,
  CreditCard,
  Store,
  Mail,
  Pencil,
  Save,
  X,
  FileDown,
  Package,
  UserCheck,
  UserX,
} from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../config/firebase";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  ParticipantFormData,
  TicketTypeOption,
  TicketInventory,
} from "../types";
import { TICKET_PRICES, SERVICE_FEE } from "../config/stripe";

interface ExportResponse {
  success: boolean;
  downloadUrl: string;
  filename: string;
}

interface PdfResponse {
  success: boolean;
  data: string;
  filename: string;
  mimeType: string;
}

interface AdminOrderParticipant {
  id: string;
  nome: string;
  cognome: string;
  email: string;
  cellulare: string;
  codiceFiscale: string;
  dataNascita: string;
  luogoNascita: string;
  via: string;
  numeroCivico: string;
  citta: string;
  ticketCode: string;
  ticketType: string;
}

interface AdminOrder {
  id: string;
  userId: string;
  totalAmount: number;
  stripePaymentId: string;
  stripeSessionId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  userName: string;
  userEmail: string;
  participants: AdminOrderParticipant[];
}

interface AdminParticipant {
  id: string;
  userId: string;
  nome: string;
  cognome: string;
  dataNascita: string;
  luogoNascita: string;
  cellulare: string;
  codiceFiscale: string;
  email: string;
  citta: string;
  via: string;
  numeroCivico: string;
  createdAt: string;
  ticketCode: string;
  ticketType: string;
  checkedIn: boolean;
  checkedInAt: string;
}

interface AdminStats {
  totalOrders: number;
  paidOrders: number;
  totalParticipants: number;
  stripeRevenue: number;
  adminManualRevenue: number;
  physicalManualRevenue: number;
}

interface PaginatedOrdersResponse {
  success: boolean;
  orders: AdminOrder[];
  total: number;
  page: number;
  pageSize: number;
}

interface PaginatedParticipantsResponse {
  success: boolean;
  participants: AdminParticipant[];
  total: number;
  page: number;
  pageSize: number;
}

type SortField = "createdAt" | "status" | "totalAmount" | "orderId";
type SortDir = "asc" | "desc";

function downloadBase64File(data: string, filename: string, mimeType: string) {
  const byteCharacters = atob(data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

const statusColors: Record<string, string> = {
  paid: "bg-green-100 text-green-800 border-green-200",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  failed: "bg-red-100 text-red-800 border-red-200",
  refunded: "bg-gray-100 text-gray-800 border-gray-200",
};

const AdminExport: React.FC = () => {
  const { currentUser, userProfile, loading: authLoading } = useAuth();
  const { t } = useLanguage();

  // State
  const [activeTab, setActiveTab] = useState<
    | "orders"
    | "participants"
    | "createTicket"
    | "createPhysicalTicket"
    | "inventory"
  >("orders");
  const [isExportingParticipants, setIsExportingParticipants] = useState(false);
  const [isExportingOrders, setIsExportingOrders] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statsLoaded, setStatsLoaded] = useState(false);

  // Data (server-paginated)
  const [ordersData, setOrdersData] = useState<AdminOrder[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [participantsData, setParticipantsData] = useState<AdminParticipant[]>(
    [],
  );
  const [participantsTotal, setParticipantsTotal] = useState(0);
  const [stats, setStats] = useState<AdminStats | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Pagination
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(20);
  const [participantsPage, setParticipantsPage] = useState(1);
  const [participantsPerPage, setParticipantsPerPage] = useState(20);

  // Update order status
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);
  const [confirmMarkPaid, setConfirmMarkPaid] = useState<string | null>(null);

  // Create Ticket state
  const emptyParticipant: ParticipantFormData = {
    nome: "",
    cognome: "",
    dataNascita: "",
    luogoNascita: "",
    cellulare: "",
    codiceFiscale: "",
    email: "",
    citta: "",
    via: "",
    numeroCivico: "",
    ticketType: "normal",
  };
  const [newParticipants, setNewParticipants] = useState<ParticipantFormData[]>(
    [{ ...emptyParticipant }],
  );
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [createTicketError, setCreateTicketError] = useState<string | null>(
    null,
  );
  const [createTicketSuccess, setCreateTicketSuccess] = useState<{
    orderId: string;
    tickets: any[];
  } | null>(null);
  const [inventory, setInventory] = useState<TicketInventory>({
    vip: 0,
    normal: 0,
  });
  const [loadingInventory, setLoadingInventory] = useState(false);

  // Physical Ticket state
  const [physicalParticipants, setPhysicalParticipants] = useState<
    ParticipantFormData[]
  >([{ ...emptyParticipant }]);
  const [isCreatingPhysicalTicket, setIsCreatingPhysicalTicket] =
    useState(false);
  const [createPhysicalTicketError, setCreatePhysicalTicketError] = useState<
    string | null
  >(null);
  const [createPhysicalTicketSuccess, setCreatePhysicalTicketSuccess] =
    useState<{
      orderId: string;
      tickets: any[];
    } | null>(null);
  const [isExportingPhysicalParticipants, setIsExportingPhysicalParticipants] =
    useState(false);
  const [isExportingCheckedIn, setIsExportingCheckedIn] = useState(false);

  // Send Reminder state
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [confirmSendReminder, setConfirmSendReminder] = useState(false);
  const [reminderResult, setReminderResult] = useState<{
    sent: number;
    failed: number;
    total: number;
  } | null>(null);

  // Send Reminder V2 state
  const [isSendingReminderV2, setIsSendingReminderV2] = useState(false);
  const [confirmSendReminderV2, setConfirmSendReminderV2] = useState(false);
  const [reminderV2Result, setReminderV2Result] = useState<{
    sent: number;
    failed: number;
    total: number;
  } | null>(null);

  // Edit Participant state
  const [editingParticipantId, setEditingParticipantId] = useState<
    string | null
  >(null);
  const [editForm, setEditForm] = useState<{
    nome: string;
    cognome: string;
    codiceFiscale: string;
    ticketCode: string;
    dataNascita: string;
    email: string;
    cellulare: string;
    ticketType: string;
  }>({
    nome: "",
    cognome: "",
    codiceFiscale: "",
    ticketCode: "",
    dataNascita: "",
    email: "",
    cellulare: "",
    ticketType: "",
  });
  const [confirmEditParticipant, setConfirmEditParticipant] = useState(false);
  const [isSavingParticipant, setIsSavingParticipant] = useState(false);
  const [editParticipantSuccess, setEditParticipantSuccess] = useState<
    string | null
  >(null);

  // Inventory Edit state
  const [editInventoryVip, setEditInventoryVip] = useState(0);
  const [editInventoryNormal, setEditInventoryNormal] = useState(0);
  const [confirmInventoryUpdate, setConfirmInventoryUpdate] = useState(false);
  const [isSavingInventory, setIsSavingInventory] = useState(false);
  const [inventorySuccess, setInventorySuccess] = useState<string | null>(null);
  const [inventoryError, setInventoryError] = useState<string | null>(null);

  // PDF Download state
  const [downloadingPdfId, setDownloadingPdfId] = useState<string | null>(null);

  // Participants sort state
  const [participantsSortDir, setParticipantsSortDir] = useState<
    "asc" | "desc"
  >("desc");

  // Participants filters
  const [checkedInFilter, setCheckedInFilter] = useState<string>("all");
  const [ticketTypeFilter, setTicketTypeFilter] = useState<string>("all");

  // Check-in state
  const [checkingInId, setCheckingInId] = useState<string | null>(null);
  const [undoingCheckInId, setUndoingCheckInId] = useState<string | null>(null);
  const [sendingEmailId, setSendingEmailId] = useState<string | null>(null);
  const [emailConfirmId, setEmailConfirmId] = useState<string | null>(null);
  const [emailSuccessVisible, setEmailSuccessVisible] = useState(false);

  const isAdmin = userProfile?.ruolo === "admin";
  const isCollaboratore = userProfile?.ruolo === "collaboratore";
  const isAdminOrCollaboratore = isAdmin || isCollaboratore;

  // Debounced search
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  // Load stats (lightweight)
  const loadStats = useCallback(async () => {
    try {
      const fn = httpsCallable<unknown, AdminStats & { success: boolean }>(
        functions,
        "getAdminStats",
      );
      const result = await fn({});
      if (result.data.success) {
        setStats({
          totalOrders: result.data.totalOrders,
          paidOrders: result.data.paidOrders,
          totalParticipants: result.data.totalParticipants,
          stripeRevenue: result.data.stripeRevenue,
          adminManualRevenue: result.data.adminManualRevenue,
          physicalManualRevenue: result.data.physicalManualRevenue,
        });
        setStatsLoaded(true);
      }
    } catch (err) {
      console.error("Load stats error:", err);
    }
  }, []);

  // Load paginated orders
  const loadOrders = useCallback(async () => {
    setIsLoadingData(true);
    setError(null);
    try {
      const fn = httpsCallable<
        {
          page: number;
          pageSize: number;
          search: string;
          statusFilter: string;
          sortField: string;
          sortDir: string;
        },
        PaginatedOrdersResponse
      >(functions, "getAdminOrders");
      const result = await fn({
        page: ordersPage,
        pageSize: ordersPerPage,
        search: debouncedSearch,
        statusFilter,
        sortField,
        sortDir,
      });
      if (result.data.success) {
        setOrdersData(result.data.orders);
        setOrdersTotal(result.data.total);
      }
    } catch (err) {
      console.error("Load orders error:", err);
      setError(t.common.exportError);
    } finally {
      setIsLoadingData(false);
    }
  }, [
    ordersPage,
    ordersPerPage,
    debouncedSearch,
    statusFilter,
    sortField,
    sortDir,
    t,
  ]);

  // Load paginated participants
  const loadParticipants = useCallback(async () => {
    setIsLoadingData(true);
    setError(null);
    try {
      const fn = httpsCallable<
        {
          page: number;
          pageSize: number;
          search: string;
          sortDir: string;
          checkedInFilter: string;
          ticketTypeFilter: string;
        },
        PaginatedParticipantsResponse
      >(functions, "getAdminParticipants");
      const result = await fn({
        page: participantsPage,
        pageSize: participantsPerPage,
        search: debouncedSearch,
        sortDir: participantsSortDir,
        checkedInFilter,
        ticketTypeFilter,
      });
      if (result.data.success) {
        setParticipantsData(result.data.participants);
        setParticipantsTotal(result.data.total);
      }
    } catch (err) {
      console.error("Load participants error:", err);
      setError(t.common.exportError);
    } finally {
      setIsLoadingData(false);
    }
  }, [
    participantsPage,
    participantsPerPage,
    debouncedSearch,
    participantsSortDir,
    checkedInFilter,
    ticketTypeFilter,
    t,
  ]);

  // Auto-load stats on mount for admins
  useEffect(() => {
    if (isAdmin) loadStats();
  }, [isAdmin, loadStats]);

  // Auto-load orders when orders tab is active
  useEffect(() => {
    if (activeTab === "orders" && isAdmin) loadOrders();
  }, [activeTab, isAdmin, loadOrders]);

  // Auto-load participants when participants tab is active
  useEffect(() => {
    if (activeTab === "participants" && isAdminOrCollaboratore)
      loadParticipants();
  }, [activeTab, isAdminOrCollaboratore, loadParticipants]);

  // Pagination totals
  const totalOrdersPages = Math.max(1, Math.ceil(ordersTotal / ordersPerPage));
  const totalParticipantsPages = Math.max(
    1,
    Math.ceil(participantsTotal / participantsPerPage),
  );

  // Export participants
  const handleExportParticipants = async () => {
    setIsExportingParticipants(true);
    setError(null);
    try {
      const fn = httpsCallable<unknown, ExportResponse>(
        functions,
        "exportParticipantsExcel",
      );
      const result = await fn({});
      if (result.data.success) {
        const link = document.createElement("a");
        link.href = result.data.downloadUrl;
        link.download = result.data.filename;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err: any) {
      console.error("Export error:", err);
      setError(t.common.exportError);
    } finally {
      setIsExportingParticipants(false);
    }
  };

  // Export orders
  const handleExportOrders = async () => {
    setIsExportingOrders(true);
    setError(null);
    try {
      const fn = httpsCallable<unknown, ExportResponse>(
        functions,
        "exportOrdersExcel",
      );
      const result = await fn({});
      if (result.data.success) {
        const link = document.createElement("a");
        link.href = result.data.downloadUrl;
        link.download = result.data.filename;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err: any) {
      console.error("Export error:", err);
      setError(t.common.exportError);
    } finally {
      setIsExportingOrders(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  // ─── Mark order as paid ───
  const handleMarkAsPaid = useCallback(
    async (orderId: string) => {
      setUpdatingOrderId(orderId);
      setError(null);
      try {
        const fn = httpsCallable<
          { orderId: string; status: string },
          { success: boolean }
        >(functions, "updateOrderStatus");
        await fn({ orderId, status: "paid" });
        setConfirmMarkPaid(null);
        // Refresh orders and stats from server
        loadOrders();
        loadStats();
      } catch (err: any) {
        console.error("Update order status error:", err);
        setError("Errore durante l'aggiornamento dello stato dell'ordine.");
      } finally {
        setUpdatingOrderId(null);
      }
    },
    [loadOrders, loadStats],
  );

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field)
      return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDir === "asc" ? (
      <ChevronUp className="w-3 h-3" />
    ) : (
      <ChevronDown className="w-3 h-3" />
    );
  };

  // ─── Confirm mark as paid dialog ───
  const ConfirmDialog = () => {
    if (!confirmMarkPaid) return null;
    const order = ordersData.find((o) => o.id === confirmMarkPaid);
    if (!order) return null;
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-bold text-gray-900 mb-2">
            Conferma aggiornamento status
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Vuoi impostare l'ordine{" "}
            <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">
              {order.id.slice(0, 12)}...
            </span>{" "}
            come <span className="font-semibold text-green-600">PAID</span>?
          </p>
          <p className="text-xs text-gray-500 mb-4">
            Importo: €{order.totalAmount.toFixed(2)} — Status attuale:{" "}
            <span
              className={`font-semibold ${order.status === "pending" ? "text-yellow-600" : "text-red-600"}`}
            >
              {order.status.toUpperCase()}
            </span>
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setConfirmMarkPaid(null)}
              className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annulla
            </button>
            <button
              onClick={() => handleMarkAsPaid(confirmMarkPaid)}
              disabled={updatingOrderId === confirmMarkPaid}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {updatingOrderId === confirmMarkPaid ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4" />
              )}
              Conferma Paid
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Create Ticket Helpers ───
  const fetchInventory = useCallback(async () => {
    setLoadingInventory(true);
    try {
      const getTicketAvailability = httpsCallable(
        functions,
        "getTicketAvailability",
      );
      const result = await getTicketAvailability();
      const data = result.data as TicketInventory;
      setInventory(data);
      setEditInventoryVip(data.vip);
      setEditInventoryNormal(data.normal);
    } catch (err) {
      console.error("Error fetching ticket availability:", err);
    } finally {
      setLoadingInventory(false);
    }
  }, []);

  // Set default tab to participants for collaboratore (they can't see orders)
  useEffect(() => {
    if (isCollaboratore && activeTab === "orders") {
      setActiveTab("participants");
    }
  }, [isCollaboratore, activeTab]);

  const handleNewParticipantChange = (
    index: number,
    data: ParticipantFormData,
  ) => {
    const updated = [...newParticipants];
    updated[index] = data;
    setNewParticipants(updated);
  };

  const addNewParticipant = () => {
    if (newParticipants.length < 10) {
      setNewParticipants([...newParticipants, { ...emptyParticipant }]);
    }
  };

  const removeNewParticipant = (index: number) => {
    if (newParticipants.length > 1) {
      setNewParticipants(newParticipants.filter((_, i) => i !== index));
    }
  };

  const validateNewParticipants = (): boolean => {
    for (const p of newParticipants) {
      if (
        !p.nome ||
        !p.cognome ||
        !p.dataNascita ||
        !p.cellulare ||
        !p.codiceFiscale ||
        !p.email
      ) {
        setCreateTicketError(
          "Tutti i campi sono obbligatori per ogni partecipante.",
        );
        return false;
      }
      if (p.codiceFiscale.length !== 16) {
        setCreateTicketError("Il codice fiscale deve essere di 16 caratteri.");
        return false;
      }
    }
    return true;
  };

  const handleCreateTicket = async () => {
    setCreateTicketError(null);
    setCreateTicketSuccess(null);

    if (!validateNewParticipants()) return;

    setIsCreatingTicket(true);

    try {
      const fn = httpsCallable(functions, "adminCreateTicket");
      const result = await fn({
        participants: newParticipants,
        adminEmail: currentUser?.email || "",
      });

      const data = result.data as {
        success: boolean;
        orderId: string;
        tickets: any[];
      };

      if (data.success) {
        setCreateTicketSuccess({
          orderId: data.orderId,
          tickets: data.tickets,
        });
        // Reset form
        setNewParticipants([{ ...emptyParticipant }]);
        // Refresh inventory
        fetchInventory();
      }
    } catch (err: any) {
      console.error("Create ticket error:", err);
      setCreateTicketError(
        err?.message || "Errore durante la creazione del biglietto.",
      );
    } finally {
      setIsCreatingTicket(false);
    }
  };

  // ─── Physical Ticket Helpers ───
  const handlePhysicalParticipantChange = (
    index: number,
    data: ParticipantFormData,
  ) => {
    const updated = [...physicalParticipants];
    updated[index] = data;
    setPhysicalParticipants(updated);
  };

  const addPhysicalParticipant = () => {
    if (physicalParticipants.length < 10) {
      setPhysicalParticipants([
        ...physicalParticipants,
        { ...emptyParticipant },
      ]);
    }
  };

  const removePhysicalParticipant = (index: number) => {
    if (physicalParticipants.length > 1) {
      setPhysicalParticipants(
        physicalParticipants.filter((_, i) => i !== index),
      );
    }
  };

  const validatePhysicalParticipants = (): boolean => {
    for (const p of physicalParticipants) {
      if (
        !p.nome ||
        !p.cognome ||
        !p.dataNascita ||
        !p.cellulare ||
        !p.codiceFiscale ||
        !p.email
      ) {
        setCreatePhysicalTicketError(
          "Tutti i campi sono obbligatori per ogni partecipante.",
        );
        return false;
      }
      if (p.codiceFiscale.length !== 16) {
        setCreatePhysicalTicketError(
          "Il codice fiscale deve essere di 16 caratteri.",
        );
        return false;
      }
    }
    return true;
  };

  const handleCreatePhysicalTicket = async () => {
    setCreatePhysicalTicketError(null);
    setCreatePhysicalTicketSuccess(null);

    if (!validatePhysicalParticipants()) return;

    setIsCreatingPhysicalTicket(true);

    try {
      const fn = httpsCallable(functions, "adminCreatePhysicalTicket");
      const result = await fn({
        participants: physicalParticipants,
        adminEmail: currentUser?.email || "",
      });

      const data = result.data as {
        success: boolean;
        orderId: string;
        tickets: any[];
      };

      if (data.success) {
        setCreatePhysicalTicketSuccess({
          orderId: data.orderId,
          tickets: data.tickets,
        });
        setPhysicalParticipants([{ ...emptyParticipant }]);
        fetchInventory();
      }
    } catch (err: any) {
      console.error("Create physical ticket error:", err);
      setCreatePhysicalTicketError(
        err?.message || "Errore durante la creazione del biglietto fisico.",
      );
    } finally {
      setIsCreatingPhysicalTicket(false);
    }
  };

  // Export checked-in participants
  const handleExportCheckedIn = async () => {
    setIsExportingCheckedIn(true);
    setError(null);
    try {
      const fn = httpsCallable<unknown, ExportResponse>(
        functions,
        "exportCheckedInParticipantsExcel",
      );
      const result = await fn({});
      if (result.data.success) {
        const link = document.createElement("a");
        link.href = result.data.downloadUrl;
        link.download = result.data.filename;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err: any) {
      console.error("Export checked-in participants error:", err);
      setError(t.common.exportError);
    } finally {
      setIsExportingCheckedIn(false);
    }
  };

  // Export physical participants
  const handleExportPhysicalParticipants = async () => {
    setIsExportingPhysicalParticipants(true);
    setError(null);
    try {
      const fn = httpsCallable<unknown, ExportResponse>(
        functions,
        "exportPhysicalParticipantsExcel",
      );
      const result = await fn({});
      if (result.data.success) {
        const link = document.createElement("a");
        link.href = result.data.downloadUrl;
        link.download = result.data.filename;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err: any) {
      console.error("Export physical participants error:", err);
      setError(t.common.exportError);
    } finally {
      setIsExportingPhysicalParticipants(false);
    }
  };

  // Send reminder email to all users
  const handleSendReminder = async () => {
    setIsSendingReminder(true);
    setError(null);
    setReminderResult(null);
    try {
      const fn = httpsCallable<
        unknown,
        { success: boolean; sent: number; failed: number; total: number }
      >(functions, "sendReminderToAllUsers");
      const result = await fn({});
      if (result.data.success) {
        setReminderResult({
          sent: result.data.sent,
          failed: result.data.failed,
          total: result.data.total,
        });
      }
    } catch (err: any) {
      console.error("Send reminder error:", err);
      setError(
        err?.message || "Errore durante l'invio delle email di promemoria.",
      );
    } finally {
      setIsSendingReminder(false);
      setConfirmSendReminder(false);
    }
  };

  // Send reminder V2 email to all users
  const handleSendReminderV2 = async () => {
    setIsSendingReminderV2(true);
    setError(null);
    setReminderV2Result(null);
    try {
      const fn = httpsCallable<
        unknown,
        { success: boolean; sent: number; failed: number; total: number }
      >(functions, "sendReminderV2ToAllUsers");
      const result = await fn({});
      if (result.data.success) {
        setReminderV2Result({
          sent: result.data.sent,
          failed: result.data.failed,
          total: result.data.total,
        });
      }
    } catch (err: any) {
      console.error("Send reminder V2 error:", err);
      setError(
        err?.message || "Errore durante l'invio delle email di promemoria V2.",
      );
    } finally {
      setIsSendingReminderV2(false);
      setConfirmSendReminderV2(false);
    }
  };

  // ─── Edit Participant Handlers ───
  const startEditParticipant = (p: AdminParticipant) => {
    setEditingParticipantId(p.id);
    setEditForm({
      nome: p.nome,
      cognome: p.cognome,
      codiceFiscale: p.codiceFiscale,
      ticketCode: p.ticketCode || "",
      dataNascita: p.dataNascita || "",
      email: p.email || "",
      cellulare: p.cellulare || "",
      ticketType: p.ticketType || "",
    });
    setEditParticipantSuccess(null);
  };

  const cancelEditParticipant = () => {
    setEditingParticipantId(null);
    setConfirmEditParticipant(false);
    setEditParticipantSuccess(null);
  };

  // Helper: calculate the price for a single ticket type (price + commission)
  const getTicketPriceWithFee = (type: string): number => {
    if (type === "child") return 0;
    const basePrice = TICKET_PRICES[type as keyof typeof TICKET_PRICES] ?? 0;
    return basePrice + SERVICE_FEE;
  };

  const handleSaveParticipant = async () => {
    if (!editingParticipantId) return;
    setIsSavingParticipant(true);
    setError(null);
    try {
      const fn = httpsCallable<
        {
          participantId: string;
          nome: string;
          cognome: string;
          codiceFiscale: string;
          ticketCode?: string;
          dataNascita?: string;
          email?: string;
          cellulare?: string;
          ticketType?: string;
        },
        { success: boolean; updatedOrderId?: string; newOrderTotal?: number }
      >(functions, "updateParticipantData");
      const result = await fn({
        participantId: editingParticipantId,
        nome: editForm.nome,
        cognome: editForm.cognome,
        codiceFiscale: editForm.codiceFiscale.toUpperCase(),
        ticketCode: editForm.ticketCode || undefined,
        dataNascita: editForm.dataNascita || undefined,
        email: editForm.email || undefined,
        cellulare: editForm.cellulare || undefined,
        ticketType: editForm.ticketType || undefined,
      });
      // Refresh from server
      loadParticipants();
      if (
        result.data.updatedOrderId &&
        result.data.newOrderTotal !== undefined
      ) {
        loadOrders();
        loadStats();
      }
      setEditParticipantSuccess(t.admin.editParticipantSuccess);
      setEditingParticipantId(null);
      setConfirmEditParticipant(false);
      setTimeout(() => setEditParticipantSuccess(null), 3000);
    } catch (err: any) {
      console.error("Update participant error:", err);
      setError(t.admin.editParticipantError);
    } finally {
      setIsSavingParticipant(false);
    }
  };

  // ─── Inventory Update Handler ───
  const handleUpdateInventory = async () => {
    setIsSavingInventory(true);
    setInventoryError(null);
    try {
      const fn = httpsCallable<
        { vip: number; normal: number },
        { success: boolean; vip: number; normal: number }
      >(functions, "updateTicketInventory");
      const result = await fn({
        vip: editInventoryVip,
        normal: editInventoryNormal,
      });
      if (result.data.success) {
        setInventory({ vip: editInventoryVip, normal: editInventoryNormal });
        setInventorySuccess(t.admin.updateInventorySuccess);
        setConfirmInventoryUpdate(false);
        setTimeout(() => setInventorySuccess(null), 3000);
      }
    } catch (err: any) {
      console.error("Update inventory error:", err);
      setInventoryError(t.admin.updateInventoryError);
    } finally {
      setIsSavingInventory(false);
    }
  };

  // ─── PDF Download Handler ───
  const handleDownloadPdf = async (participantId: string) => {
    setDownloadingPdfId(participantId);
    setError(null);
    try {
      const fn = httpsCallable<{ participantId: string }, PdfResponse>(
        functions,
        "adminDownloadTicketPDF",
      );
      const result = await fn({ participantId });
      if (result.data.success) {
        downloadBase64File(
          result.data.data,
          result.data.filename,
          result.data.mimeType,
        );
      }
    } catch (err: any) {
      console.error("Download PDF error:", err);
      const msg = err?.message || "";
      if (msg.includes("Nessun biglietto") || msg.includes("not-found")) {
        setError(t.admin.downloadPdfNoTicket);
      } else {
        setError(t.admin.downloadPdfError);
      }
    } finally {
      setDownloadingPdfId(null);
    }
  };

  // ─── Check-in Handler ───
  const handleCheckIn = async (ticketCode: string, participantId: string) => {
    setCheckingInId(participantId);
    setError(null);
    try {
      const fn = httpsCallable<
        { ticketCode: string },
        { valid: boolean; message: string }
      >(functions, "checkInTicket");
      const result = await fn({ ticketCode });
      if (result.data.valid) {
        loadParticipants();
      } else {
        setError(result.data.message);
      }
    } catch (err: any) {
      console.error("Check-in error:", err);
      setError(err?.message || "Errore durante il check-in.");
    } finally {
      setCheckingInId(null);
    }
  };

  // ─── Undo Check-in Handler ───
  const handleUndoCheckIn = async (
    ticketCode: string,
    participantId: string,
  ) => {
    setUndoingCheckInId(participantId);
    setError(null);
    try {
      const fn = httpsCallable<
        { ticketCode: string },
        { valid: boolean; message: string }
      >(functions, "undoCheckInTicket");
      const result = await fn({ ticketCode });
      if (result.data.valid) {
        loadParticipants();
      } else {
        setError(result.data.message);
      }
    } catch (err: any) {
      console.error("Undo check-in error:", err);
      setError(err?.message || "Errore durante l'annullamento del check-in.");
    } finally {
      setUndoingCheckInId(null);
    }
  };

  // ─── Resend Ticket Email Handler ───
  const handleResendTicketEmail = async (participantId: string) => {
    setSendingEmailId(participantId);
    setEmailConfirmId(null);
    setError(null);
    try {
      const fn = httpsCallable<{ participantId: string }, { success: boolean }>(
        functions,
        "adminResendTicketEmail",
      );
      await fn({ participantId });
      setEmailSuccessVisible(true);
    } catch (err: any) {
      console.error("Resend ticket email error:", err);
      setError(
        err?.message || "Errore durante l'invio dell'email del biglietto.",
      );
    } finally {
      setSendingEmailId(null);
    }
  };

  // Early returns AFTER all hooks (React rules of hooks)
  if (!authLoading && (!currentUser || !isAdminOrCollaboratore)) {
    return <Navigate to="/" replace />;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-romanian-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gray-50">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-romanian-blue/10 rounded-full mb-4">
            <FileSpreadsheet className="w-8 h-8 text-romanian-blue" />
          </div>
          <h1 className="font-display text-3xl font-bold text-romanian-blue mb-2">
            {t.admin.title}
          </h1>
          <p className="text-gray-600">{t.admin.subtitle}</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    {t.admin.totalOrders}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalOrders}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <TicketIcon className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    {t.admin.paidOrders}
                  </p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.paidOrders}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    {t.admin.totalParticipants}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalParticipants}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-green-200 p-5 bg-gradient-to-br from-green-50 to-emerald-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Euro className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <p className="text-xs text-green-700 uppercase tracking-wide font-semibold">
                    {t.admin.totalRevenue}
                  </p>
                  <p className="text-2xl font-bold text-green-700">
                    €{stats.stripeRevenue.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-5 bg-gradient-to-br from-amber-50 to-yellow-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <p className="text-xs text-amber-700 uppercase tracking-wide font-semibold">
                    {t.admin.adminManualRevenue}
                  </p>
                  <p className="text-2xl font-bold text-amber-700">
                    €{stats.adminManualRevenue.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-orange-200 p-5 bg-gradient-to-br from-orange-50 to-red-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Store className="w-5 h-5 text-orange-700" />
                </div>
                <div>
                  <p className="text-xs text-orange-700 uppercase tracking-wide font-semibold">
                    {t.admin.physicalManualRevenue}
                  </p>
                  <p className="text-2xl font-bold text-orange-700">
                    €{stats.physicalManualRevenue.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send Reminder Confirmation Dialog */}
        {confirmSendReminder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {t.admin.sendReminderConfirmTitle}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t.admin.sendReminderConfirmDesc}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmSendReminder(false)}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t.admin.cancel || "Annulla"}
                </button>
                <button
                  onClick={handleSendReminder}
                  disabled={isSendingReminder}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {isSendingReminder ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  {isSendingReminder
                    ? t.admin.sendingReminder
                    : t.admin.confirmSend}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Send Reminder V2 Confirmation Dialog */}
        {confirmSendReminderV2 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {t.admin.sendReminderV2ConfirmTitle}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t.admin.sendReminderV2ConfirmDesc}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmSendReminderV2(false)}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t.admin.cancel || "Annulla"}
                </button>
                <button
                  onClick={handleSendReminderV2}
                  disabled={isSendingReminderV2}
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {isSendingReminderV2 ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  {isSendingReminderV2
                    ? t.admin.sendingReminderV2
                    : t.admin.confirmSend}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reminder Result */}
        {reminderResult && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3 mb-6">
            <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-purple-800 font-semibold">
                {t.admin.reminderSentSuccess}
              </p>
              <p className="text-purple-700 text-sm mt-1">
                {t.admin.reminderSentDetail
                  .replace("{sent}", String(reminderResult.sent))
                  .replace("{total}", String(reminderResult.total))
                  .replace("{failed}", String(reminderResult.failed))}
              </p>
            </div>
            <button
              onClick={() => setReminderResult(null)}
              className="ml-auto text-purple-400 hover:text-purple-600 text-lg font-bold"
            >
              ×
            </button>
          </div>
        )}

        {/* Reminder V2 Result */}
        {reminderV2Result && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3 mb-6">
            <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-indigo-800 font-semibold">
                {t.admin.reminderV2SentSuccess}
              </p>
              <p className="text-indigo-700 text-sm mt-1">
                {t.admin.reminderV2SentDetail
                  .replace("{sent}", String(reminderV2Result.sent))
                  .replace("{total}", String(reminderV2Result.total))
                  .replace("{failed}", String(reminderV2Result.failed))}
              </p>
            </div>
            <button
              onClick={() => setReminderV2Result(null)}
              className="ml-auto text-indigo-400 hover:text-indigo-600 text-lg font-bold"
            >
              ×
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3 mb-6">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Tabs + Actions Bar */}
        {(activeTab === "createTicket" ||
          activeTab === "createPhysicalTicket" ||
          activeTab === "inventory" ||
          statsLoaded ||
          isCollaboratore) && (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between p-4 gap-4">
                {/* Tabs */}
                <div className="flex flex-wrap gap-1 bg-gray-100 rounded-lg p-1">
                  {isAdmin && (
                    <button
                      onClick={() => {
                        setActiveTab("orders");
                        setSearchQuery("");
                        setOrdersPage(1);
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        activeTab === "orders"
                          ? "bg-white text-romanian-blue shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      {t.admin.tabOrders}
                      {stats && (
                        <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                          {stats.totalOrders}
                        </span>
                      )}
                    </button>
                  )}
                  {isAdminOrCollaboratore && (
                    <button
                      onClick={() => {
                        setActiveTab("participants");
                        setSearchQuery("");
                        setParticipantsPage(1);
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        activeTab === "participants"
                          ? "bg-white text-romanian-blue shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <Users className="w-4 h-4" />
                      {t.admin.tabParticipants}
                      {stats && (
                        <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                          {stats.totalParticipants}
                        </span>
                      )}
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => {
                        setActiveTab("createTicket");
                        setSearchQuery("");
                        fetchInventory();
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        activeTab === "createTicket"
                          ? "bg-white text-romanian-blue shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <Plus className="w-4 h-4" />
                      {t.admin.tabCreateTicket}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setActiveTab("createPhysicalTicket");
                      setSearchQuery("");
                      fetchInventory();
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      activeTab === "createPhysicalTicket"
                        ? "bg-white text-orange-600 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <Store className="w-4 h-4" />
                    {t.admin.tabCreatePhysicalTicket}
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => {
                        setActiveTab("inventory");
                        setSearchQuery("");
                        fetchInventory();
                        setEditInventoryVip(inventory.vip);
                        setEditInventoryNormal(inventory.normal);
                      }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        activeTab === "inventory"
                          ? "bg-white text-indigo-600 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <Package className="w-4 h-4" />
                      {t.admin.tabInventory}
                    </button>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
                  {/* Data Management Actions - Admin only */}
                  {isAdmin && (
                    <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <div className="text-xs text-slate-600 font-semibold tracking-wide uppercase mr-2 hidden lg:block">
                        📊 Gestione Dati
                      </div>
                      <button
                        onClick={handleExportParticipants}
                        disabled={isExportingParticipants}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-romanian-blue text-white rounded-lg hover:bg-romanian-blue/90 transition-colors"
                      >
                        {isExportingParticipants ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        {isExportingParticipants
                          ? t.admin.exporting
                          : t.admin.exportParticipants}
                      </button>
                      <button
                        onClick={handleExportOrders}
                        disabled={isExportingOrders}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        {isExportingOrders ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        {isExportingOrders
                          ? t.admin.exporting
                          : t.admin.exportOrders}
                      </button>
                      <button
                        onClick={handleExportCheckedIn}
                        disabled={isExportingCheckedIn}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        {isExportingCheckedIn ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        {isExportingCheckedIn
                          ? t.admin.exporting
                          : t.admin.exportCheckedIn}
                      </button>
                      {isAdmin && (
                        <button
                          onClick={handleExportPhysicalParticipants}
                          disabled={isExportingPhysicalParticipants}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                        >
                          {isExportingPhysicalParticipants ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          {isExportingPhysicalParticipants
                            ? t.admin.exporting
                            : t.admin.exportPhysicalParticipants}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Divider */}
                  {isAdmin && (
                    <div className="hidden lg:block w-px h-12 bg-gray-300"></div>
                  )}

                  {/* Communication Actions */}
                  {isAdmin && (
                    <div className="flex flex-wrap items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <div className="text-xs text-purple-700 font-semibold tracking-wide uppercase mr-2 hidden lg:block">
                        ✉️ Comunicazione
                      </div>
                      <button
                        onClick={() => setConfirmSendReminder(true)}
                        disabled={isSendingReminder}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium shadow-sm"
                      >
                        {isSendingReminder ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4" />
                        )}
                        {isSendingReminder
                          ? t.admin.sendingReminder
                          : t.admin.sendReminder}
                      </button>
                      <button
                        onClick={() => setConfirmSendReminderV2(true)}
                        disabled={isSendingReminderV2}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium shadow-sm"
                      >
                        {isSendingReminderV2 ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4" />
                        )}
                        {isSendingReminderV2
                          ? t.admin.sendingReminderV2
                          : t.admin.sendReminderV2}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 px-4 pb-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      if (activeTab === "orders") setOrdersPage(1);
                      else setParticipantsPage(1);
                    }}
                    placeholder={t.admin.search}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/20 focus:border-romanian-blue"
                  />
                </div>

                {/* Status filter (orders tab only) */}
                {activeTab === "orders" && (
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setOrdersPage(1);
                    }}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/20 bg-white"
                  >
                    <option value="all">{t.admin.allStatuses}</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                  </select>
                )}

                {/* Participants filters */}
                {activeTab === "participants" && (
                  <>
                    <select
                      value={checkedInFilter}
                      onChange={(e) => {
                        setCheckedInFilter(e.target.value);
                        setParticipantsPage(1);
                      }}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/20 bg-white"
                    >
                      <option value="all">Tutti (Check-in)</option>
                      <option value="checkedIn">✓ Check-in effettuato</option>
                      <option value="notCheckedIn">✗ Non check-in</option>
                    </select>
                    <select
                      value={ticketTypeFilter}
                      onChange={(e) => {
                        setTicketTypeFilter(e.target.value);
                        setParticipantsPage(1);
                      }}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/20 bg-white"
                    >
                      <option value="all">Tutti i tipi</option>
                      <option value="vip">VIP</option>
                      <option value="normal">Normal</option>
                      <option value="child">Bambino</option>
                    </select>
                  </>
                )}
              </div>
            </div>

            {/* Refresh Data Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
              <div className="flex items-center justify-center p-4">
                <button
                  onClick={() => {
                    if (isAdmin) loadStats();
                    if (activeTab === "orders") loadOrders();
                    else if (activeTab === "participants") loadParticipants();
                  }}
                  disabled={isLoadingData}
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
                >
                  <RefreshCw
                    className={`w-5 h-5 ${isLoadingData ? "animate-spin" : ""}`}
                  />
                  {isLoadingData ? t.admin.loading : t.admin.refreshData}
                </button>
              </div>
            </div>

            {/* ORDERS TABLE */}
            {activeTab === "orders" && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <ConfirmDialog />
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          Azioni
                        </th>
                        <th
                          className="text-left px-4 py-3 font-semibold text-gray-600 cursor-pointer hover:text-gray-900 select-none whitespace-nowrap"
                          onClick={() => handleSort("createdAt")}
                        >
                          <span className="inline-flex items-center gap-1">
                            {t.admin.orderDate} <SortIcon field="createdAt" />
                          </span>
                        </th>
                        <th
                          className="text-left px-4 py-3 font-semibold text-gray-600 cursor-pointer hover:text-gray-900 select-none whitespace-nowrap"
                          onClick={() => handleSort("orderId")}
                        >
                          <span className="inline-flex items-center gap-1">
                            {t.admin.orderId} <SortIcon field="orderId" />
                          </span>
                        </th>
                        <th
                          className="text-left px-4 py-3 font-semibold text-gray-600 cursor-pointer hover:text-gray-900 select-none whitespace-nowrap"
                          onClick={() => handleSort("status")}
                        >
                          <span className="inline-flex items-center gap-1">
                            {t.admin.status} <SortIcon field="status" />
                          </span>
                        </th>
                        <th
                          className="text-right px-4 py-3 font-semibold text-gray-600 cursor-pointer hover:text-gray-900 select-none whitespace-nowrap"
                          onClick={() => handleSort("totalAmount")}
                        >
                          <span className="inline-flex items-center gap-1">
                            {t.admin.amount} <SortIcon field="totalAmount" />
                          </span>
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.stripePaymentId}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.updatedAt}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.user}
                        </th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.nParticipants}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.ticketCode}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.ticketType || "Tipo Accesso"}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.name}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.surname}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.email}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.phone}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.fiscalCode}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.birthDate}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.birthPlace}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.street}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.streetNumber}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.city}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ordersData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={20}
                            className="text-center py-12 text-gray-400"
                          >
                            {isLoadingData ? (
                              <div className="flex items-center justify-center gap-2">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                {t.admin.loading}
                              </div>
                            ) : (
                              t.admin.noData
                            )}
                          </td>
                        </tr>
                      ) : (
                        ordersData.map((order) => {
                          const orderParticipants = order.participants;
                          const rowCount = Math.max(
                            orderParticipants.length,
                            1,
                          );
                          return orderParticipants.length === 0 ? (
                            <tr
                              key={order.id}
                              className="hover:bg-gray-50/50 transition-colors"
                            >
                              <td
                                className="px-4 py-3 whitespace-nowrap"
                                rowSpan={rowCount}
                              >
                                {order.status !== "paid" ? (
                                  <button
                                    onClick={() => setConfirmMarkPaid(order.id)}
                                    disabled={updatingOrderId === order.id}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                                    title="Segna come Paid"
                                  >
                                    {updatingOrderId === order.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <CreditCard className="w-3 h-3" />
                                    )}
                                    Paid
                                  </button>
                                ) : (
                                  <span className="text-green-500 text-xs">
                                    ✓
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                {order.createdAt}
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                  {order.id.slice(0, 10)}...
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[order.status] || "bg-gray-100 text-gray-800"}`}
                                >
                                  {order.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-semibold">
                                €{order.totalAmount.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs text-gray-500">
                                {order.stripePaymentId
                                  ? order.stripePaymentId.slice(0, 16) + "..."
                                  : "—"}
                              </td>
                              <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                                {order.updatedAt || "—"}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {order.userName ||
                                  order.userId.slice(0, 10) + "..."}
                              </td>
                              <td className="px-4 py-3 text-center">0</td>
                              <td className="px-4 py-3" colSpan={12}></td>
                            </tr>
                          ) : (
                            orderParticipants.map((p, pIdx) => {
                              const tc = p.ticketCode || "";
                              const tt = p.ticketType || "";
                              return (
                                <tr
                                  key={`${order.id}-${pIdx}`}
                                  className={`hover:bg-gray-50/50 transition-colors ${pIdx > 0 ? "border-t border-gray-50" : ""}`}
                                >
                                  {pIdx === 0 && (
                                    <>
                                      <td
                                        className="px-4 py-3 whitespace-nowrap"
                                        rowSpan={rowCount}
                                      >
                                        {order.status !== "paid" ? (
                                          <button
                                            onClick={() =>
                                              setConfirmMarkPaid(order.id)
                                            }
                                            disabled={
                                              updatingOrderId === order.id
                                            }
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                                            title="Segna come Paid"
                                          >
                                            {updatingOrderId === order.id ? (
                                              <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : (
                                              <CreditCard className="w-3 h-3" />
                                            )}
                                            Paid
                                          </button>
                                        ) : (
                                          <span className="text-green-500 text-xs">
                                            ✓
                                          </span>
                                        )}
                                      </td>
                                      <td
                                        className="px-4 py-3 text-gray-600 whitespace-nowrap"
                                        rowSpan={rowCount}
                                      >
                                        {order.createdAt}
                                      </td>
                                      <td
                                        className="px-4 py-3"
                                        rowSpan={rowCount}
                                      >
                                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                          {order.id.slice(0, 10)}...
                                        </span>
                                      </td>
                                      <td
                                        className="px-4 py-3"
                                        rowSpan={rowCount}
                                      >
                                        <span
                                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[order.status] || "bg-gray-100 text-gray-800"}`}
                                        >
                                          {order.status}
                                        </span>
                                      </td>
                                      <td
                                        className="px-4 py-3 text-right font-semibold"
                                        rowSpan={rowCount}
                                      >
                                        €{order.totalAmount.toFixed(2)}
                                      </td>
                                      <td
                                        className="px-4 py-3 font-mono text-xs text-gray-500"
                                        rowSpan={rowCount}
                                      >
                                        {order.stripePaymentId
                                          ? order.stripePaymentId.slice(0, 16) +
                                            "..."
                                          : "—"}
                                      </td>
                                      <td
                                        className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs"
                                        rowSpan={rowCount}
                                      >
                                        {order.updatedAt || "—"}
                                      </td>
                                      <td
                                        className="px-4 py-3 text-gray-600"
                                        rowSpan={rowCount}
                                      >
                                        {order.userName ? (
                                          <div>
                                            <p className="font-medium text-gray-800">
                                              {order.userName}
                                            </p>
                                            <p className="text-xs text-gray-400">
                                              {order.userEmail}
                                            </p>
                                          </div>
                                        ) : (
                                          <span className="text-gray-400 text-xs">
                                            {order.userId.slice(0, 10)}...
                                          </span>
                                        )}
                                      </td>
                                      <td
                                        className="px-4 py-3 text-center font-semibold"
                                        rowSpan={rowCount}
                                      >
                                        {order.participants.length}
                                      </td>
                                    </>
                                  )}
                                  <td className="px-4 py-3">
                                    {tc ? (
                                      <span className="font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-[10px]">
                                        {tc}
                                      </span>
                                    ) : (
                                      <span className="text-gray-300">—</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    {tt ? (
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                          tt === "vip"
                                            ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                                            : tt === "child"
                                              ? "bg-green-100 text-green-800 border border-green-300"
                                              : "bg-blue-100 text-blue-800 border border-blue-300"
                                        }`}
                                      >
                                        {tt.toUpperCase()}
                                      </span>
                                    ) : (
                                      <span className="text-gray-300">—</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                                    {p?.nome || "—"}
                                  </td>
                                  <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                                    {p?.cognome || "—"}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600 text-xs">
                                    {p?.email || ""}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                    {p?.cellulare || ""}
                                  </td>
                                  <td className="px-4 py-3 font-mono text-xs text-gray-600">
                                    {p?.codiceFiscale || ""}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                    {p?.dataNascita || ""}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                    {p?.luogoNascita || ""}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                    {p?.via || ""}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600">
                                    {p?.numeroCivico || ""}
                                  </td>
                                  <td className="px-4 py-3 text-gray-600">
                                    {p?.citta || ""}
                                  </td>
                                </tr>
                              );
                            })
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Orders summary footer */}
                <div className="border-t border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="text-sm text-gray-600">
                      {ordersTotal} ordini totali
                    </div>
                    {stats && (
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Euro className="w-5 h-5 text-green-700" />
                          <span className="text-sm font-medium text-green-700">
                            {t.admin.totalRevenue}:
                          </span>
                          <span className="text-xl font-bold text-green-700">
                            €{stats.stripeRevenue.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-5 h-5 text-amber-700" />
                          <span className="text-sm font-medium text-amber-700">
                            {t.admin.adminManualRevenue}:
                          </span>
                          <span className="text-xl font-bold text-amber-700">
                            €{stats.adminManualRevenue.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Store className="w-5 h-5 text-orange-700" />
                          <span className="text-sm font-medium text-orange-700">
                            {t.admin.physicalManualRevenue}:
                          </span>
                          <span className="text-xl font-bold text-orange-700">
                            €{stats.physicalManualRevenue.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Orders Pagination */}
                {totalOrdersPages > 1 && (
                  <div className="border-t border-gray-200 bg-white px-4 py-3">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span>Righe per pagina:</span>
                        <select
                          value={ordersPerPage}
                          onChange={(e) => {
                            setOrdersPerPage(Number(e.target.value));
                            setOrdersPage(1);
                          }}
                          className="px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/20"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            setOrdersPage((p) => Math.max(1, p - 1))
                          }
                          disabled={ordersPage === 1}
                          className="px-3 py-1 border border-gray-200 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Precedente
                        </button>
                        <span className="text-sm text-gray-600">
                          Pagina {ordersPage} di {totalOrdersPages}
                        </span>
                        <button
                          onClick={() =>
                            setOrdersPage((p) =>
                              Math.min(totalOrdersPages, p + 1),
                            )
                          }
                          disabled={ordersPage === totalOrdersPages}
                          className="px-3 py-1 border border-gray-200 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Successiva
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* PARTICIPANTS TABLE */}
            {activeTab === "participants" && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Confirm Edit Dialog */}
                {confirmEditParticipant && editingParticipantId && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {t.admin.editParticipantTitle}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        {t.admin.editParticipantConfirm}
                      </p>
                      <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1">
                        <div>
                          <span className="font-semibold">{t.admin.name}:</span>{" "}
                          {editForm.nome}
                        </div>
                        <div>
                          <span className="font-semibold">
                            {t.admin.surname}:
                          </span>{" "}
                          {editForm.cognome}
                        </div>
                        <div>
                          <span className="font-semibold">
                            {t.admin.fiscalCode}:
                          </span>{" "}
                          {editForm.codiceFiscale.toUpperCase()}
                        </div>
                        {editForm.dataNascita && (
                          <div>
                            <span className="font-semibold">
                              {t.admin.birthDate}:
                            </span>{" "}
                            {editForm.dataNascita}
                          </div>
                        )}
                        {editForm.email && (
                          <div>
                            <span className="font-semibold">
                              {t.admin.email}:
                            </span>{" "}
                            {editForm.email}
                          </div>
                        )}
                        {editForm.cellulare && (
                          <div>
                            <span className="font-semibold">
                              {t.admin.phone}:
                            </span>{" "}
                            {editForm.cellulare}
                          </div>
                        )}
                        {editForm.ticketCode && (
                          <div>
                            <span className="font-semibold">
                              {t.admin.ticketCode}:
                            </span>{" "}
                            {editForm.ticketCode}
                          </div>
                        )}
                        {editForm.ticketType && (
                          <div>
                            <span className="font-semibold">
                              {t.admin.ticketType}:
                            </span>{" "}
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                editForm.ticketType === "vip"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : editForm.ticketType === "child"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {editForm.ticketType.toUpperCase()}
                            </span>{" "}
                            <span className="text-gray-500">
                              (€
                              {getTicketPriceWithFee(
                                editForm.ticketType,
                              ).toFixed(2)}
                              )
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-3 justify-end">
                        <button
                          onClick={() => setConfirmEditParticipant(false)}
                          className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          {t.admin.cancelEdit}
                        </button>
                        <button
                          onClick={handleSaveParticipant}
                          disabled={isSavingParticipant}
                          className="px-4 py-2 text-sm bg-romanian-blue text-white rounded-lg hover:bg-romanian-blue/90 transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                          {isSavingParticipant ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          {isSavingParticipant
                            ? t.admin.saving
                            : t.admin.confirmUpdate}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Edit Success */}
                {editParticipantSuccess && (
                  <div className="bg-green-50 border-b border-green-200 px-4 py-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-green-700 text-sm font-medium">
                      {editParticipantSuccess}
                    </span>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          Invia Email
                        </th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          Check-in
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.actions}
                        </th>
                        <th
                          className="text-left px-4 py-3 font-semibold text-gray-600 cursor-pointer hover:text-gray-900 select-none whitespace-nowrap"
                          onClick={() =>
                            setParticipantsSortDir(
                              participantsSortDir === "asc" ? "desc" : "asc",
                            )
                          }
                        >
                          <span className="inline-flex items-center gap-1">
                            {t.admin.date}
                            {participantsSortDir === "asc" ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )}
                          </span>
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.ticketCode}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.ticketType || "Tipo Biglietto"}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.name}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.surname}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.email}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.phone}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.fiscalCode}
                        </th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 whitespace-nowrap">
                          {t.admin.birthDate}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {participantsData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={12}
                            className="text-center py-12 text-gray-400"
                          >
                            {isLoadingData ? (
                              <div className="flex items-center justify-center gap-2">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                {t.admin.loading}
                              </div>
                            ) : (
                              t.admin.noData
                            )}
                          </td>
                        </tr>
                      ) : (
                        participantsData.map((p, idx) => {
                          const tc = p.ticketCode || "";
                          const tt = p.ticketType || "";
                          const isEditing = editingParticipantId === p.id;
                          return (
                            <tr
                              key={p.id}
                              className={`hover:bg-gray-50/50 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/30"} ${isEditing ? "ring-2 ring-romanian-blue/20 bg-blue-50/30" : ""}`}
                            >
                              {/* Send Email column */}
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                {tc && isAdminOrCollaboratore ? (
                                  <button
                                    onClick={() => setEmailConfirmId(p.id)}
                                    disabled={sendingEmailId === p.id}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50 font-medium"
                                    title="Invia email con biglietto"
                                  >
                                    {sendingEmailId === p.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Mail className="w-3 h-3" />
                                    )}
                                  </button>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                              {/* Check-in column */}
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                {tc ? (
                                  <div className="flex items-center justify-center gap-1">
                                    {p.checkedIn ? (
                                      <>
                                        <span
                                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-100 text-green-700 border border-green-200 rounded-full font-medium"
                                          title={
                                            p.checkedInAt
                                              ? `Check-in: ${p.checkedInAt}`
                                              : ""
                                          }
                                        >
                                          <UserCheck className="w-3 h-3" />✓
                                        </span>
                                        <button
                                          onClick={() =>
                                            handleUndoCheckIn(tc, p.id)
                                          }
                                          disabled={undoingCheckInId === p.id}
                                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                                          title="Annulla check-in"
                                        >
                                          {undoingCheckInId === p.id ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <UserX className="w-3 h-3" />
                                          )}
                                          Undo
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        onClick={() => handleCheckIn(tc, p.id)}
                                        disabled={checkingInId === p.id}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 font-medium"
                                        title="Effettua check-in"
                                      >
                                        {checkingInId === p.id ? (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                          <UserCheck className="w-3 h-3" />
                                        )}
                                        Check-in
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-300 text-xs">
                                    —
                                  </span>
                                )}
                              </td>
                              {/* Actions column */}
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-1">
                                  {isAdmin && !isEditing && (
                                    <button
                                      onClick={() => startEditParticipant(p)}
                                      className="p-1.5 text-gray-400 hover:text-romanian-blue hover:bg-blue-50 rounded-md transition-colors"
                                      title={t.admin.editParticipant}
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  {isAdmin && isEditing && (
                                    <>
                                      <button
                                        onClick={() => {
                                          if (
                                            !editForm.nome ||
                                            !editForm.cognome ||
                                            !editForm.codiceFiscale
                                          )
                                            return;
                                          if (
                                            editForm.codiceFiscale.length !== 16
                                          ) {
                                            setError(
                                              t.admin.editParticipantError,
                                            );
                                            return;
                                          }
                                          setConfirmEditParticipant(true);
                                        }}
                                        className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                                        title={t.admin.save}
                                      >
                                        <Save className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={cancelEditParticipant}
                                        className="p-1.5 text-red-400 hover:bg-red-50 rounded-md transition-colors"
                                        title={t.admin.cancelEdit}
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </>
                                  )}
                                  {isAdminOrCollaboratore && tc && (
                                    <button
                                      onClick={() => handleDownloadPdf(p.id)}
                                      disabled={downloadingPdfId === p.id}
                                      className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors disabled:opacity-50"
                                      title={t.admin.downloadPdf}
                                    >
                                      {downloadingPdfId === p.id ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      ) : (
                                        <FileDown className="w-3.5 h-3.5" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                {p.createdAt}
                              </td>
                              <td className="px-4 py-3">
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={editForm.ticketCode}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        ticketCode: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-romanian-blue/30"
                                  />
                                ) : tc ? (
                                  <span className="font-mono bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs">
                                    {tc}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {isEditing ? (
                                  <select
                                    value={editForm.ticketType}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        ticketType: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/30 bg-white"
                                  >
                                    <option value="vip">
                                      VIP (€
                                      {(
                                        TICKET_PRICES.vip + SERVICE_FEE
                                      ).toFixed(2)}
                                      )
                                    </option>
                                    <option value="normal">
                                      NORMAL (€
                                      {(
                                        TICKET_PRICES.normal + SERVICE_FEE
                                      ).toFixed(2)}
                                      )
                                    </option>
                                    <option value="child">CHILD (€0.00)</option>
                                  </select>
                                ) : tt ? (
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      tt === "vip"
                                        ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                                        : tt === "child"
                                          ? "bg-green-100 text-green-800 border border-green-300"
                                          : "bg-blue-100 text-blue-800 border border-blue-300"
                                    }`}
                                  >
                                    {tt.toUpperCase()}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-800">
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={editForm.nome}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        nome: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/30"
                                  />
                                ) : (
                                  p.nome
                                )}
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-800">
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={editForm.cognome}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        cognome: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/30"
                                  />
                                ) : (
                                  p.cognome
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {isEditing ? (
                                  <input
                                    type="email"
                                    value={editForm.email}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        email: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/30"
                                  />
                                ) : (
                                  p.email
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {isEditing ? (
                                  <input
                                    type="tel"
                                    value={editForm.cellulare}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        cellulare: e.target.value,
                                      })
                                    }
                                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/30"
                                  />
                                ) : (
                                  p.cellulare
                                )}
                              </td>
                              <td className="px-4 py-3 font-mono text-xs text-gray-600">
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={editForm.codiceFiscale}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        codiceFiscale:
                                          e.target.value.toUpperCase(),
                                      })
                                    }
                                    maxLength={16}
                                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-romanian-blue/30"
                                  />
                                ) : (
                                  p.codiceFiscale
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={editForm.dataNascita}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        dataNascita: e.target.value,
                                      })
                                    }
                                    placeholder="DD/MM/YYYY"
                                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/30"
                                  />
                                ) : (
                                  p.dataNascita
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
                  <div className="text-sm text-gray-600">
                    {participantsTotal} {t.admin.tabParticipants.toLowerCase()}
                  </div>
                </div>

                {/* Participants Pagination */}
                {totalParticipantsPages > 1 && (
                  <div className="border-t border-gray-200 bg-white px-4 py-3">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span>Righe per pagina:</span>
                        <select
                          value={participantsPerPage}
                          onChange={(e) => {
                            setParticipantsPerPage(Number(e.target.value));
                            setParticipantsPage(1);
                          }}
                          className="px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-romanian-blue/20"
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            setParticipantsPage((p) => Math.max(1, p - 1))
                          }
                          disabled={participantsPage === 1}
                          className="px-3 py-1 border border-gray-200 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Precedente
                        </button>
                        <span className="text-sm text-gray-600">
                          Pagina {participantsPage} di {totalParticipantsPages}
                        </span>
                        <button
                          onClick={() =>
                            setParticipantsPage((p) =>
                              Math.min(totalParticipantsPages, p + 1),
                            )
                          }
                          disabled={participantsPage === totalParticipantsPages}
                          className="px-3 py-1 border border-gray-200 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Successiva
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CREATE TICKET TAB */}
            {activeTab === "createTicket" && (
              <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-romanian-blue/10 rounded-lg flex items-center justify-center">
                      <TicketIcon className="w-5 h-5 text-romanian-blue" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {t.admin.createTicketTitle}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {t.admin.createTicketDesc}
                      </p>
                    </div>
                  </div>

                  {/* Inventory info */}
                  {!loadingInventory && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                      <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-center">
                        <span className="text-yellow-800 font-semibold text-sm">
                          VIP: {inventory.vip} disponibili
                        </span>
                      </div>
                      <div className="p-3 bg-blue-50 border border-blue-300 rounded-lg text-center">
                        <span className="text-blue-800 font-semibold text-sm">
                          Normal: {inventory.normal} disponibili
                        </span>
                      </div>
                      <div className="p-3 bg-green-50 border border-green-300 rounded-lg text-center">
                        <span className="text-green-800 font-semibold text-sm">
                          Bambino: Gratuito
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Success Message */}
                {createTicketSuccess && (
                  <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-800 text-lg">
                          {t.admin.ticketCreatedSuccess}
                        </p>
                        <p className="text-green-700 text-sm mt-1">
                          {t.admin.ticketCreatedOrderId}:{" "}
                          <span className="font-mono">
                            {createTicketSuccess.orderId}
                          </span>
                        </p>
                        <div className="mt-3 space-y-1">
                          {createTicketSuccess.tickets.map(
                            (tk: any, idx: number) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 text-sm text-green-700"
                              >
                                <TicketIcon className="w-4 h-4" />
                                <span className="font-mono">
                                  {tk.ticketCode}
                                </span>
                                <span className="uppercase text-xs font-medium bg-green-200 px-2 py-0.5 rounded-full">
                                  {tk.ticketType}
                                </span>
                                {tk.pdfUrl && (
                                  <a
                                    href={tk.pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-800 underline flex items-center gap-1"
                                  >
                                    <Download className="w-3 h-3" /> PDF
                                  </a>
                                )}
                              </div>
                            ),
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setCreateTicketSuccess(null);
                            setNewParticipants([{ ...emptyParticipant }]);
                          }}
                          className="mt-4 btn-primary text-sm"
                        >
                          {t.admin.createAnother}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {createTicketError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-red-700 text-sm">
                      {createTicketError}
                    </span>
                  </div>
                )}

                {/* Participant Forms */}
                {!createTicketSuccess && (
                  <>
                    {newParticipants.map((participant, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-4"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg text-gray-900">
                            {t.admin.tabParticipants} {index + 1}
                          </h3>
                          {newParticipants.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeNewParticipant(index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>

                        {/* Ticket Type Selector */}
                        <div className="mb-4">
                          <label className="label">
                            {t.admin.ticketType} *
                          </label>
                          <select
                            value={participant.ticketType}
                            onChange={(e) => {
                              handleNewParticipantChange(index, {
                                ...participant,
                                ticketType: e.target.value as TicketTypeOption,
                              });
                            }}
                            className="input"
                          >
                            <option value="vip" disabled={inventory.vip <= 0}>
                              VIP (Partecipante) - €
                              {(TICKET_PRICES.vip + SERVICE_FEE).toFixed(2)}
                              {inventory.vip <= 0 ? " (Esaurito)" : ""}
                            </option>
                            <option
                              value="normal"
                              disabled={inventory.normal <= 0}
                            >
                              Normal (Spettatore) - €
                              {(TICKET_PRICES.normal + SERVICE_FEE).toFixed(2)}
                              {inventory.normal <= 0 ? " (Esaurito)" : ""}
                            </option>
                            <option value="child">
                              Bambino - €{TICKET_PRICES.child.toFixed(2)}
                            </option>
                          </select>
                          <div className="mt-1 space-y-0.5">
                            <p className="text-sm text-gray-600">
                              Biglietto: €
                              {TICKET_PRICES[participant.ticketType].toFixed(2)}
                            </p>
                            {participant.ticketType !== "child" && (
                              <p className="text-sm text-gray-500">
                                Commissione: €{SERVICE_FEE.toFixed(2)}
                              </p>
                            )}
                            <p className="text-sm font-semibold text-romanian-blue">
                              Totale: €
                              {(
                                TICKET_PRICES[participant.ticketType] +
                                (participant.ticketType !== "child"
                                  ? SERVICE_FEE
                                  : 0)
                              ).toFixed(2)}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="label">{t.admin.name} *</label>
                            <input
                              type="text"
                              value={participant.nome}
                              onChange={(e) =>
                                handleNewParticipantChange(index, {
                                  ...participant,
                                  nome: e.target.value,
                                })
                              }
                              required
                              className="input"
                              placeholder="Nome"
                            />
                          </div>
                          <div>
                            <label className="label">{t.admin.surname} *</label>
                            <input
                              type="text"
                              value={participant.cognome}
                              onChange={(e) =>
                                handleNewParticipantChange(index, {
                                  ...participant,
                                  cognome: e.target.value,
                                })
                              }
                              required
                              className="input"
                              placeholder="Cognome"
                            />
                          </div>
                          <div>
                            <label className="label">
                              {t.admin.birthDate} *
                            </label>
                            <input
                              type="date"
                              value={participant.dataNascita}
                              onChange={(e) =>
                                handleNewParticipantChange(index, {
                                  ...participant,
                                  dataNascita: e.target.value,
                                })
                              }
                              required
                              className="input"
                            />
                          </div>

                          <div>
                            <label className="label">{t.admin.phone} *</label>
                            <input
                              type="tel"
                              value={participant.cellulare}
                              onChange={(e) =>
                                handleNewParticipantChange(index, {
                                  ...participant,
                                  cellulare: e.target.value,
                                })
                              }
                              required
                              className="input"
                              placeholder="+39 393 581 7177"
                            />
                          </div>
                          <div>
                            <label className="label">
                              {t.admin.fiscalCode} *
                            </label>
                            <input
                              type="text"
                              value={participant.codiceFiscale}
                              onChange={(e) =>
                                handleNewParticipantChange(index, {
                                  ...participant,
                                  codiceFiscale: e.target.value.toUpperCase(),
                                })
                              }
                              required
                              maxLength={16}
                              className="input uppercase"
                              placeholder="RSSMRA85M01H501Z"
                            />
                          </div>
                          <div>
                            <label className="label">{t.admin.email} *</label>
                            <input
                              type="email"
                              value={participant.email}
                              onChange={(e) =>
                                handleNewParticipantChange(index, {
                                  ...participant,
                                  email: e.target.value,
                                })
                              }
                              required
                              className="input"
                              placeholder="email@esempio.com"
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add Participant + Summary */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
                      <button
                        onClick={addNewParticipant}
                        disabled={newParticipants.length >= 10}
                        className="btn-outline flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        {t.admin.addParticipant}
                      </button>

                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          {newParticipants.length} partecipante/i — Totale:{" "}
                          <span className="font-bold text-romanian-blue text-lg">
                            €
                            {newParticipants
                              .reduce(
                                (sum, p) =>
                                  sum +
                                  TICKET_PRICES[p.ticketType] +
                                  (p.ticketType !== "child" ? SERVICE_FEE : 0),
                                0,
                              )
                              .toFixed(2)}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Create Button */}
                    <div className="mt-6">
                      <button
                        onClick={handleCreateTicket}
                        disabled={isCreatingTicket}
                        className="btn-primary w-full flex items-center justify-center gap-2 py-4 text-lg"
                      >
                        {isCreatingTicket ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            {t.admin.creating}
                          </>
                        ) : (
                          <>
                            <TicketIcon className="w-5 h-5" />
                            {t.admin.createTicketBtn}
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* CREATE PHYSICAL TICKET TAB */}
            {activeTab === "createPhysicalTicket" && (
              <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border border-orange-200 p-6 mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Store className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {t.admin.createPhysicalTicketTitle}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {t.admin.createPhysicalTicketDesc}
                      </p>
                    </div>
                  </div>

                  {/* Inventory info */}
                  {!loadingInventory && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                      <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-center">
                        <span className="text-yellow-800 font-semibold text-sm">
                          VIP: {inventory.vip} disponibili
                        </span>
                      </div>
                      <div className="p-3 bg-blue-50 border border-blue-300 rounded-lg text-center">
                        <span className="text-blue-800 font-semibold text-sm">
                          Normal: {inventory.normal} disponibili
                        </span>
                      </div>
                      <div className="p-3 bg-green-50 border border-green-300 rounded-lg text-center">
                        <span className="text-green-800 font-semibold text-sm">
                          Bambino: Gratuito
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Success Message */}
                {createPhysicalTicketSuccess && (
                  <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-800 text-lg">
                          {t.admin.physicalTicketCreatedSuccess}
                        </p>
                        <p className="text-green-700 text-sm mt-1">
                          {t.admin.ticketCreatedOrderId}:{" "}
                          <span className="font-mono">
                            {createPhysicalTicketSuccess.orderId}
                          </span>
                        </p>
                        <div className="mt-3 space-y-1">
                          {createPhysicalTicketSuccess.tickets.map(
                            (tk: any, idx: number) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 text-sm text-green-700"
                              >
                                <TicketIcon className="w-4 h-4" />
                                <span className="font-mono">
                                  {tk.ticketCode}
                                </span>
                                <span className="uppercase text-xs font-medium bg-green-200 px-2 py-0.5 rounded-full">
                                  {tk.ticketType}
                                </span>
                                {tk.pdfUrl && (
                                  <a
                                    href={tk.pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-800 underline flex items-center gap-1"
                                  >
                                    <Download className="w-3 h-3" /> PDF
                                  </a>
                                )}
                              </div>
                            ),
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setCreatePhysicalTicketSuccess(null);
                            setPhysicalParticipants([{ ...emptyParticipant }]);
                          }}
                          className="mt-4 btn-primary text-sm"
                        >
                          {t.admin.createAnother}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {createPhysicalTicketError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-red-700 text-sm">
                      {createPhysicalTicketError}
                    </span>
                  </div>
                )}

                {/* Participant Forms */}
                {!createPhysicalTicketSuccess && (
                  <>
                    {physicalParticipants.map((participant, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-xl shadow-sm border border-orange-100 p-6 mb-4"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-semibold text-lg text-gray-900">
                            {t.admin.tabParticipants} {index + 1}
                          </h3>
                          {physicalParticipants.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removePhysicalParticipant(index)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>

                        {/* Ticket Type Selector */}
                        <div className="mb-4">
                          <label className="label">
                            {t.admin.ticketType} *
                          </label>
                          <select
                            value={participant.ticketType}
                            onChange={(e) => {
                              handlePhysicalParticipantChange(index, {
                                ...participant,
                                ticketType: e.target.value as TicketTypeOption,
                              });
                            }}
                            className="input"
                          >
                            <option value="vip" disabled={inventory.vip <= 0}>
                              VIP (Partecipante) - €
                              {(TICKET_PRICES.vip + SERVICE_FEE).toFixed(2)}
                              {inventory.vip <= 0 ? " (Esaurito)" : ""}
                            </option>
                            <option
                              value="normal"
                              disabled={inventory.normal <= 0}
                            >
                              Normal (Spettatore) - €
                              {(TICKET_PRICES.normal + SERVICE_FEE).toFixed(2)}
                              {inventory.normal <= 0 ? " (Esaurito)" : ""}
                            </option>
                            <option value="child">
                              Bambino - €{TICKET_PRICES.child.toFixed(2)}
                            </option>
                          </select>
                          <div className="mt-1 space-y-0.5">
                            <p className="text-sm text-gray-600">
                              Biglietto: €
                              {TICKET_PRICES[participant.ticketType].toFixed(2)}
                            </p>
                            {participant.ticketType !== "child" && (
                              <p className="text-sm text-gray-500">
                                Commissione: €{SERVICE_FEE.toFixed(2)}
                              </p>
                            )}
                            <p className="text-sm font-semibold text-orange-600">
                              Totale: €
                              {(
                                TICKET_PRICES[participant.ticketType] +
                                (participant.ticketType !== "child"
                                  ? SERVICE_FEE
                                  : 0)
                              ).toFixed(2)}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="label">{t.admin.name} *</label>
                            <input
                              type="text"
                              value={participant.nome}
                              onChange={(e) =>
                                handlePhysicalParticipantChange(index, {
                                  ...participant,
                                  nome: e.target.value,
                                })
                              }
                              required
                              className="input"
                              placeholder="Nome"
                            />
                          </div>
                          <div>
                            <label className="label">{t.admin.surname} *</label>
                            <input
                              type="text"
                              value={participant.cognome}
                              onChange={(e) =>
                                handlePhysicalParticipantChange(index, {
                                  ...participant,
                                  cognome: e.target.value,
                                })
                              }
                              required
                              className="input"
                              placeholder="Cognome"
                            />
                          </div>
                          <div>
                            <label className="label">
                              {t.admin.birthDate} *
                            </label>
                            <input
                              type="date"
                              value={participant.dataNascita}
                              onChange={(e) =>
                                handlePhysicalParticipantChange(index, {
                                  ...participant,
                                  dataNascita: e.target.value,
                                })
                              }
                              required
                              className="input"
                            />
                          </div>

                          <div>
                            <label className="label">{t.admin.phone} *</label>
                            <input
                              type="tel"
                              value={participant.cellulare}
                              onChange={(e) =>
                                handlePhysicalParticipantChange(index, {
                                  ...participant,
                                  cellulare: e.target.value,
                                })
                              }
                              required
                              className="input"
                              placeholder="+39 393 581 7177"
                            />
                          </div>
                          <div>
                            <label className="label">
                              {t.admin.fiscalCode} *
                            </label>
                            <input
                              type="text"
                              value={participant.codiceFiscale}
                              onChange={(e) =>
                                handlePhysicalParticipantChange(index, {
                                  ...participant,
                                  codiceFiscale: e.target.value.toUpperCase(),
                                })
                              }
                              required
                              maxLength={16}
                              className="input uppercase"
                              placeholder="RSSMRA85M01H501Z"
                            />
                          </div>
                          <div>
                            <label className="label">{t.admin.email} *</label>
                            <input
                              type="email"
                              value={participant.email}
                              onChange={(e) =>
                                handlePhysicalParticipantChange(index, {
                                  ...participant,
                                  email: e.target.value,
                                })
                              }
                              required
                              className="input"
                              placeholder="email@esempio.com"
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add Participant + Summary */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
                      <button
                        onClick={addPhysicalParticipant}
                        disabled={physicalParticipants.length >= 10}
                        className="btn-outline flex items-center gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
                      >
                        <Plus className="w-4 h-4" />
                        {t.admin.addParticipant}
                      </button>

                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          {physicalParticipants.length} partecipante/i — Totale:{" "}
                          <span className="font-bold text-orange-600 text-lg">
                            €
                            {physicalParticipants
                              .reduce(
                                (sum, p) =>
                                  sum +
                                  TICKET_PRICES[p.ticketType] +
                                  (p.ticketType !== "child" ? SERVICE_FEE : 0),
                                0,
                              )
                              .toFixed(2)}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Create Button */}
                    <div className="mt-6">
                      <button
                        onClick={handleCreatePhysicalTicket}
                        disabled={isCreatingPhysicalTicket}
                        className="w-full flex items-center justify-center gap-2 py-4 text-lg bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold disabled:opacity-50"
                      >
                        {isCreatingPhysicalTicket ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            {t.admin.creating}
                          </>
                        ) : (
                          <>
                            <Store className="w-5 h-5" />
                            {t.admin.createPhysicalTicketBtn}
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* TICKET INVENTORY TAB */}
            {activeTab === "inventory" && (
              <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border border-indigo-200 p-6 mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {t.admin.ticketInventoryTitle}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {t.admin.ticketInventoryDesc}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Current Inventory Display */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t.admin.currentInventory}
                  </h3>
                  {loadingInventory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-yellow-50 border border-yellow-300 rounded-xl text-center">
                        <p className="text-xs text-yellow-700 uppercase tracking-wide font-semibold mb-1">
                          {t.admin.vipTickets}
                        </p>
                        <p className="text-3xl font-bold text-yellow-800">
                          {inventory.vip}
                        </p>
                      </div>
                      <div className="p-4 bg-blue-50 border border-blue-300 rounded-xl text-center">
                        <p className="text-xs text-blue-700 uppercase tracking-wide font-semibold mb-1">
                          {t.admin.normalTickets}
                        </p>
                        <p className="text-3xl font-bold text-blue-800">
                          {inventory.normal}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Inventory Success */}
                {inventorySuccess && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-green-700 text-sm font-medium">
                      {inventorySuccess}
                    </span>
                    <button
                      onClick={() => setInventorySuccess(null)}
                      className="ml-auto text-green-400 hover:text-green-600 text-lg font-bold"
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Inventory Error */}
                {inventoryError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-red-700 text-sm">
                      {inventoryError}
                    </span>
                    <button
                      onClick={() => setInventoryError(null)}
                      className="ml-auto text-red-400 hover:text-red-600 text-lg font-bold"
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Edit Inventory Form */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t.admin.updateInventory}
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.admin.vipTickets}
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={editInventoryVip}
                        onChange={(e) =>
                          setEditInventoryVip(
                            Math.max(0, Number(e.target.value)),
                          )
                        }
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.admin.normalTickets}
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={editInventoryNormal}
                        onChange={(e) =>
                          setEditInventoryNormal(
                            Math.max(0, Number(e.target.value)),
                          )
                        }
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => setConfirmInventoryUpdate(true)}
                    className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    {t.admin.updateInventory}
                  </button>
                </div>

                {/* Confirm Inventory Update Dialog */}
                {confirmInventoryUpdate && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {t.admin.updateInventory}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        {t.admin.updateInventoryConfirm}
                      </p>
                      <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm space-y-1">
                        <div>
                          <span className="font-semibold">
                            {t.admin.vipTickets}:
                          </span>{" "}
                          {inventory.vip} →{" "}
                          <span className="font-bold text-indigo-600">
                            {editInventoryVip}
                          </span>
                        </div>
                        <div>
                          <span className="font-semibold">
                            {t.admin.normalTickets}:
                          </span>{" "}
                          {inventory.normal} →{" "}
                          <span className="font-bold text-indigo-600">
                            {editInventoryNormal}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-3 justify-end">
                        <button
                          onClick={() => setConfirmInventoryUpdate(false)}
                          className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          {t.admin.cancelEdit}
                        </button>
                        <button
                          onClick={handleUpdateInventory}
                          disabled={isSavingInventory}
                          className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                        >
                          {isSavingInventory ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          {isSavingInventory
                            ? t.admin.updatingInventory
                            : t.admin.confirmUpdate}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Resend Ticket Email Confirmation Modal */}
      {emailConfirmId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                Invia email biglietto
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Vuoi reinviare l'email con il biglietto in PDF a questo
              partecipante?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setEmailConfirmId(null)}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={() => handleResendTicketEmail(emailConfirmId)}
                disabled={sendingEmailId === emailConfirmId}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {sendingEmailId === emailConfirmId ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                {sendingEmailId === emailConfirmId
                  ? "Invio in corso..."
                  : "Invia email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resend Ticket Email Success Modal */}
      {emailSuccessVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4 text-center">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Email inviata!
            </h3>
            <p className="text-sm text-gray-600 mb-5">
              L'email con il biglietto in PDF è stata inviata con successo al
              partecipante.
            </p>
            <button
              onClick={() => setEmailSuccessVisible(false)}
              className="px-6 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminExport;
